'use client';

import type { ReactNode } from 'react';

import { SetupStepFooter } from '@/components/setup/experience/SetupStepFooter';
import { cn } from '@/lib/utils';

interface SetupConnectFormActionsProps {
  backLabel?: string;
  onBack?: () => void;
  showBack?: boolean;
  submitLabel: string;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  /** Left-aligned slot shown when Back is hidden (e.g. a Skip control on the first step). */
  leading?: ReactNode;
}

/** Standard form footer — same Back/Submit bar as every other setup step. */
export function SetupConnectFormActions({
  backLabel,
  onBack,
  showBack = true,
  submitLabel,
  isSubmitting = false,
  submitDisabled = false,
  leading,
}: SetupConnectFormActionsProps) {
  const showBackButton = showBack && onBack;

  return (
    <SetupStepFooter
      backLabel={backLabel ?? ''}
      onBack={onBack ?? (() => {})}
      backHidden={!showBackButton}
      backDisabled={isSubmitting}
      primaryLabel={submitLabel}
      primaryType="submit"
      primaryDisabled={submitDisabled}
      isPrimaryLoading={isSubmitting}
      leading={leading}
    />
  );
}

interface SetupConnectFormPanelProps {
  children: ReactNode;
  className?: string;
}

export function SetupConnectFormPanel({ children, className }: SetupConnectFormPanelProps) {
  return <div className={cn('flex flex-col gap-[0.875rem] pb-6', className)}>{children}</div>;
}

/** Muted note box shown at the bottom of a step body (mock/disclaimer copy). */
export function SetupConnectDisclaimer({ children }: { children: ReactNode }) {
  return (
    <p className="m-0 rounded-[0.625rem] border border-[color-mix(in_oklch,var(--border)_85%,transparent)] bg-[color-mix(in_oklch,var(--muted)_55%,var(--card))] px-4 py-3.5 text-[0.8125rem] leading-[1.55] text-[color-mix(in_oklch,var(--foreground)_88%,var(--muted-foreground))]">
      {children}
    </p>
  );
}
